import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Button, IconButton, FormControl, Select, MenuItem, Collapse, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import dayjs from "dayjs";
import {
  useGetTOUListQuery,
  useAddTOUMutation,
  useUpdateBulkTOUMutation,
  useDeleteTOUMutation,
} from "../api/financialApiSlice";
import CSVBulkUpdate from "../UserSettings/CSVBulkUpdate";

const LABEL_SX = {
  fontSize: "0.58rem", fontWeight: 600, letterSpacing: "0.08em",
  textTransform: "uppercase", color: "rgba(255,255,255,0.3)", mb: "5px",
};

const INFO_TOOLTIP_SX = {
  tooltip: {
    sx: {
      bgcolor: "#1C232C", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "8px",
      p: "12px 14px", maxWidth: 300, boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
    },
  },
  arrow: { sx: { color: "#1C232C" } },
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#0d1117", borderRadius: "6px", fontSize: "0.84rem", color: "#e0e6f0",
    "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.22)" },
    "&.Mui-focused fieldset": { borderColor: "#FDB940 !important", borderWidth: "1px !important" },
  },
  "& .MuiInputBase-input": { padding: "7px 10px" },
  "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.4)" },
};

const SELECT_SX = {
  backgroundColor: "#0d1117", borderRadius: "6px", fontSize: "0.84rem", color: "#e0e6f0",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.1)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.22)" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FDB940 !important" },
  "& .MuiSelect-icon": { color: "rgba(255,255,255,0.4)" },
  "& .MuiSelect-select": { padding: "7px 10px" },
};

const MENU_PROPS = {
  PaperProps: {
    sx: {
      backgroundColor: "#1C232C", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px",
      "& .MuiMenuItem-root": {
        fontSize: "0.84rem", color: "#e0e6f0",
        "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
        "&.Mui-selected": { bgcolor: "rgba(253,185,64,0.1)" },
      },
    },
  },
};

const PERIOD_COLORS = {
  Peak:     { bg: "rgba(253,185,64,0.15)",  color: "#FDB940", border: "rgba(253,185,64,0.35)"  },
  Standard: { bg: "rgba(74,222,128,0.1)",   color: "#4ade80", border: "rgba(74,222,128,0.3)"   },
  OffPeak:  { bg: "rgba(147,197,253,0.1)",  color: "#93C5FD", border: "rgba(147,197,253,0.3)"  },
};

function PeriodChip({ value }) {
  const c = PERIOD_COLORS[value] || { bg: "rgba(255,255,255,0.08)", color: "#e0e6f0", border: "rgba(255,255,255,0.15)" };
  return (
    <Box sx={{ bgcolor: c.bg, color: c.color, border: `1px solid ${c.border}`, borderRadius: "6px", px: "8px", py: "3px", fontSize: "0.75rem", fontWeight: 600, display: "inline-block" }}>
      {value}
    </Box>
  );
}

export default function TOU() {
  const { data: tou_data, isSuccess, refetch } = useGetTOUListQuery();
  const [addTOU, { isSuccess: isAddSuccess }] = useAddTOUMutation();
  const [updateBulkTOU, { isSuccess: isUpdateSuccess, isError: isUpdateError }] = useUpdateBulkTOUMutation();

  const [bulkArray, setBulkArray] = useState([]);
  const [saved, setSaved] = useState("Saved!");
  const [deletingId, setDeletingId] = useState(null);
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [newRowId, setNewRowId] = useState(null);
  const [pendingScroll, setPendingScroll] = useState(false);
  const prevIdsRef = useRef(new Set());

  React.useEffect(() => { if (isSuccess) setBulkArray(tou_data); }, [isSuccess, tou_data]);
  React.useEffect(() => {
    if (isAddSuccess) { refetch(); setPendingScroll(true); }
  }, [refetch, isAddSuccess]);
  React.useEffect(() => {
    if (pendingScroll && tou_data?.length > 0) {
      const addedId = tou_data.find((t) => !prevIdsRef.current.has(t.id))?.id;
      if (addedId) { setNewRowId(addedId); setPendingScroll(false); }
    }
  }, [tou_data, pendingScroll]);
  React.useEffect(() => {
    if (isUpdateSuccess) setSaved("Saved!");
    else if (isUpdateError) setSaved("Save");
  }, [isUpdateSuccess, isUpdateError]);

  const handleAdd = () => {
    prevIdsRef.current = new Set(tou_data?.map((t) => t.id) ?? []);
    addTOU({ body: { period: "Peak", start_date: "", end_date: "", start: "", end: "", day: "Weekday" } });
  };

  if (!isSuccess) return null;

  return (
    <Box sx={{ backgroundColor: "#1C232C", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "12px", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", px: "16px", py: "12px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <AccessTimeIcon sx={{ fontSize: "1rem", color: "#FDB940", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#fff" }}>Time of Use</Typography>
        <Tooltip
          title={
            <Box>
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#fff", mb: "4px" }}>Time-of-use periods</Typography>
              <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.65)", lineHeight: 1.5 }}>
                A period maps part of the day to one of three rate classes — Peak, Standard or Off-Peak — by day and time. Each row applies over the date range set on it.
              </Typography>
            </Box>
          }
          placement="bottom"
          arrow
          componentsProps={INFO_TOOLTIP_SX}
        >
          <InfoOutlinedIcon sx={{ fontSize: "14px", color: "rgba(255,255,255,0.3)", cursor: "help", transition: "color 0.18s", "&:hover": { color: "#FDB940" } }} />
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small" onClick={handleAdd} sx={{ color: "rgba(255,255,255,0.45)", "&:hover": { color: "#FDB940" } }}>
          <AddIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
        <IconButton size="small" onClick={() => setCsvModalOpen(true)} sx={{ color: "rgba(255,255,255,0.45)", "&:hover": { color: "#FDB940" } }}>
          <UploadFileIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      {/* Rows */}
      <Box sx={{ p: "12px", flexGrow: 1 }}>
        {tou_data?.map((tou) => (
          <TOURow
            key={tou.id}
            tou={tou}
            bulkArray={bulkArray}
            setBulkArray={setBulkArray}
            setSaved={setSaved}
            deletingId={deletingId}
            setDeletingId={setDeletingId}
            isNew={tou.id === newRowId}
            onFirstEdit={tou.id === newRowId ? () => setNewRowId(null) : undefined}
          />
        ))}
        {tou_data?.length === 0 && (
          <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.3)", textAlign: "center", py: "16px" }}>
            No TOU entries. Click + to add one.
          </Typography>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ px: "16px", py: "10px", borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          disabled={saved !== "Save"}
          onClick={() => updateBulkTOU({ body: bulkArray })}
          sx={{
            bgcolor: "#FDB940", color: "#0d1117", fontWeight: 600, fontSize: "0.78rem",
            textTransform: "none", borderRadius: "8px", boxShadow: "none", px: "16px", py: "5px",
            "&:hover": { bgcolor: "#e8a830", boxShadow: "none" },
            "&.Mui-disabled": { bgcolor: "rgba(253,185,64,0.25)", color: "rgba(0,0,0,0.4)" },
          }}
        >
          {saved}
        </Button>
      </Box>

      <CSVBulkUpdate
        open={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        csvType="tou-data"
        onSuccess={() => { refetch(); setSaved("Save"); }}
      />
    </Box>
  );
}

function TOURow({ tou, bulkArray, setBulkArray, setSaved, deletingId, setDeletingId, isNew, onFirstEdit }) {
  const [deleteTOU] = useDeleteTOUMutation();
  const saTimeOffset = 2 * 60 * 60 * 1000;
  const rowRef = useRef(null);

  useEffect(() => {
    if (isNew && rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [isNew]);

  const [period, setPeriod] = useState(tou.period);
  const [day, setDay] = useState(tou.day);
  const [startTime, setStartTime] = useState(dayjs("2023-01-01T" + tou.start));
  const [endTime, setEndTime] = useState(dayjs("2023-01-01T" + tou.end));
  const [startDate, setStartDate] = useState(tou.start_date ? dayjs(tou.start_date) : null);
  const [endDate, setEndDate] = useState(tou.end_date ? dayjs(tou.end_date) : null);

  const update = (field, value) => {
    setBulkArray((prev) => prev.map((item) => item.id === tou.id ? { ...item, [field]: value } : item));
    setSaved("Save");
    if (onFirstEdit) onFirstEdit();
  };

  const onChangeStart = (time) => {
    const utc = time.subtract(2, "hours");
    setStartTime(utc);
    update("start", utc.$H + ":" + utc.$m + ":" + utc.$s);
  };

  const onChangeEnd = (time) => {
    const utc = time.subtract(2, "hours");
    setEndTime(utc);
    update("end", utc.$H + ":" + utc.$m + ":" + utc.$s);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box ref={rowRef} sx={{ mb: "6px" }}>
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: "12px", px: "14px", py: "10px", backgroundColor: "#161B22", border: isNew ? "1px solid rgba(74,222,128,0.6)" : "1px solid rgba(255,255,255,0.06)", borderRadius: "8px", transition: "border-color 0.2s" }}>

          {/* Period */}
          <Box sx={{ flex: 1.2, minWidth: 0 }}>
            <Typography sx={LABEL_SX}>Period</Typography>
            <Select
              value={period}
              onChange={(e) => { setPeriod(e.target.value); update("period", e.target.value); }}
              size="small"
              fullWidth
              renderValue={(val) => <PeriodChip value={val} />}
              sx={{ bgcolor: isNew ? "rgba(253,185,64,0.06)" : "transparent", borderRadius: "6px", "& .MuiOutlinedInput-notchedOutline": { border: isNew ? "1px solid #FDB940" : "none" }, "& .MuiSelect-select": { padding: "4px 20px 4px 6px !important" }, "& .MuiSelect-icon": { color: "rgba(255,255,255,0.3)", right: 0 } }}
              MenuProps={MENU_PROPS}
            >
              {["Peak", "Standard", "OffPeak"].map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </Box>

          {/* Day */}
          <Box sx={{ flex: 1.2, minWidth: 0 }}>
            <Typography sx={LABEL_SX}>Day</Typography>
            <FormControl size="small" fullWidth>
              <Select value={day} onChange={(e) => { setDay(e.target.value); update("day", e.target.value); }} sx={SELECT_SX} MenuProps={MENU_PROPS}>
                {["Weekday", "Saturday", "Sunday"].map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          {/* Start */}
          <Box sx={{ flex: 1.3, minWidth: 0 }}>
            <Typography sx={LABEL_SX}>Start</Typography>
            <TimePicker value={startTime.add(saTimeOffset, "ms")} onChange={onChangeStart} ampm={false} slotProps={{ textField: { size: "small", sx: INPUT_SX } }} />
          </Box>

          {/* End */}
          <Box sx={{ flex: 1.3, minWidth: 0 }}>
            <Typography sx={LABEL_SX}>End</Typography>
            <TimePicker value={endTime.add(saTimeOffset, "ms")} onChange={onChangeEnd} ampm={false} slotProps={{ textField: { size: "small", sx: INPUT_SX } }} />
          </Box>

          {/* From date */}
          <Box sx={{ flex: 1.5, minWidth: 0 }}>
            <Typography sx={LABEL_SX}>From</Typography>
            <MobileDatePicker
              format="YYYY/MM/DD"
              value={startDate}
              onChange={(date) => {
                const d = date.set("hour", 0).set("minute", 0).set("second", 0);
                setStartDate(d);
                update("start_date", d.format("YYYY-MM-DD HH:mm:ss"));
              }}
              slotProps={{ textField: { size: "small", fullWidth: true, sx: INPUT_SX } }}
            />
          </Box>

          {/* To date */}
          <Box sx={{ flex: 1.5, minWidth: 0 }}>
            <Typography sx={LABEL_SX}>To</Typography>
            <MobileDatePicker
              format="YYYY/MM/DD"
              value={endDate}
              onChange={(date) => {
                const d = date.set("hour", 21).set("minute", 59).set("second", 59);
                setEndDate(d);
                update("end_date", d.format("YYYY-MM-DD HH:mm:ss"));
              }}
              slotProps={{ textField: { size: "small", fullWidth: true, sx: INPUT_SX } }}
            />
          </Box>

          <IconButton size="small" onClick={() => setDeletingId(tou.id)}
            sx={{ color: "rgba(255,255,255,0.3)", "&:hover": { color: "#f87171", bgcolor: "rgba(248,113,113,0.08)" }, flexShrink: 0 }}>
            <DeleteIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Collapse in={deletingId === tou.id}>
          <Box sx={{ mt: "3px", px: "12px", py: "8px", backgroundColor: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: "7px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.6)" }}>Delete this TOU entry?</Typography>
            <Box sx={{ display: "flex", gap: "6px" }}>
              <Button size="small" onClick={() => setDeletingId(null)} sx={{ fontSize: "0.75rem", textTransform: "none", color: "rgba(255,255,255,0.5)" }}>Cancel</Button>
              <Button size="small" onClick={() => { deleteTOU({ tou_id: tou.id }); setDeletingId(null); }}
                sx={{ fontSize: "0.75rem", textTransform: "none", color: "#f87171", border: "1px solid rgba(248,113,113,0.35)", borderRadius: "5px", px: "8px", "&:hover": { bgcolor: "rgba(248,113,113,0.1)" } }}>
                Delete
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </LocalizationProvider>
  );
}
